import {useEffect, useState} from 'react';

import {createVersionPoller} from '@afriex/core/utils';

// 5 minutes: frequent enough to surface a new deploy during a live session,
// infrequent enough to not matter for cost/noise.
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

async function fetchLatestVersion(): Promise<string> {
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch version.json: ${response.status}`);
  }
  const data = (await response.json()) as {version?: string};
  if (!data.version) {
    throw new Error('version.json is missing a version field');
  }
  return data.version;
}

/**
 * Detects when the deployed build has moved on from the version the page
 * currently has loaded, by polling `/version.json` (emitted by the Vite
 * build, see vite.config.ts) on an interval and whenever the tab regains
 * focus. Returns true once, the first time a different version is seen.
 */
export function useNewVersionAvailable(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const poller = createVersionPoller({
      currentVersion: __APP_VERSION__,
      fetchLatestVersion,
      onNewVersionDetected: () => setUpdateAvailable(true),
      intervalMs: VERSION_CHECK_INTERVAL_MS,
    });

    poller.start();
    void poller.checkNow();

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void poller.checkNow();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      poller.stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return updateAvailable;
}
